import React, { useEffect, useState, useCallback } from "react";
import { Loader2, CheckCircle2, XCircle, Plus, Minus, Download, RefreshCw, ChevronDown, ChevronRight, FileDown, Send, Bell, Store, Sparkles, Repeat, ScrollText, ShieldCheck, ExternalLink, ClipboardList, Copy, X } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(" ");
  return d || e.message || "Something went wrong.";
};

const STATUS_STYLE = {
  active: "bg-[#0B6B4F]/15 text-[#3DBB8E] border-[#0B6B4F]/40",
  suspended: "bg-[#B8860B]/15 text-[#E4C97A] border-[#B8860B]/40",
  closed: "bg-[#3a3a30]/40 text-[#9A9A90] border-[#3a3a30]",
};
const Badge = ({ s }) => (
  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] font-bold rounded-full border ${STATUS_STYLE[s] || STATUS_STYLE.closed}`}>{s}</span>
);

export const SecuritiesAdmin = ({ client }) => {
  const [apps, setApps] = useState({ pending: [], resolved: [], pending_count: 0 });
  const [accounts, setAccounts] = useState([]);
  const [loans, setLoans] = useState([]);
  const [requests, setRequests] = useState({ pending: [], resolved: [], pending_count: 0 });
  const [feeIncome, setFeeIncome] = useState({ fee_income: [], total_remitted: 0 });
  const [market, setMarket] = useState({ listings: [], tradelines: [], resale_active: [], transfers: [], tradeline_revenue: 0, resale_revenue: 0 });
  const [totalBalance, setTotalBalance] = useState(0);
  const [activeOnLoan, setActiveOnLoan] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, c, l, rq, fi, mk] = await Promise.all([
        client.get("/admin/securities/applications"),
        client.get("/admin/securities/accounts"),
        client.get("/admin/securities/loans"),
        client.get("/admin/securities/loan-requests"),
        client.get("/admin/securities/fee-income"),
        client.get("/admin/securities/listings"),
      ]);
      setApps(a.data);
      setAccounts(c.data.accounts || []);
      setTotalBalance(c.data.total_balance || 0);
      setLoans(l.data.loans || []);
      setActiveOnLoan(l.data.active_on_loan || 0);
      setRequests(rq.data);
      setFeeIncome(fi.data);
      setMarket(mk.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [client]);

  useEffect(() => { load(); }, [load]);

  const download1099 = async () => {
    try {
      const r = await client.get("/admin/securities/reports/1099-oid.csv", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "seutrade_1099_oid.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (loading) return <Loader2 className="animate-spin text-[#8A8A8A]" />;

  return (
    <div className="space-y-8" data-testid="securities-admin">
      <div className="flex flex-wrap gap-4">
        <Kpi label="Total ledger balance (USD)" value={fmt(totalBalance)} color="#3DBB8E" />
        <Kpi label="Active accounts" value={accounts.filter((a) => a.status === "active").length} color="#C9A227" />
        <Kpi label="On loan (USD)" value={fmt(activeOnLoan)} color="#9DB4E8" />
        <Kpi label="Fees remitted to lenders" value={fmt(feeIncome.total_remitted)} color="#7DD3B8" />
        <Kpi label="Pending applications" value={apps.pending_count} color="#E4C97A" />
        <button onClick={load} className="ml-auto self-start flex items-center gap-1.5 text-[11px] text-[#8A8A8A] hover:text-[#ECECE4]" data-testid="securities-admin-refresh"><RefreshCw size={12} /> Refresh</button>
      </div>

      <PayoutsDashboard client={client} feeIncome={feeIncome} onChange={load} />

      {/* Pending applications */}
      <div>
        <h3 className="text-[13px] uppercase tracking-[0.14em] font-bold text-[#E4C97A] mb-3">Pending Applications</h3>
        {apps.pending.length === 0 ? <p className="text-[#5C5C5C] text-sm">No pending applications.</p> : (
          <div className="space-y-3">
            {apps.pending.map((a) => <PendingApp key={a.id} app={a} client={client} onDone={load} />)}
          </div>
        )}
      </div>

      {/* Accounts */}
      <div>
        <h3 className="text-[13px] uppercase tracking-[0.14em] font-bold text-[#3DBB8E] mb-3">Provisioned Accounts</h3>
        {accounts.length === 0 ? <p className="text-[#5C5C5C] text-sm">No accounts provisioned yet.</p> : (
          <div className="space-y-3">
            {accounts.map((acc) => <AccountRow key={acc.id} acc={acc} client={client} onChange={load} />)}
          </div>
        )}
      </div>

      {/* Counterparty borrow requests */}
      <div>
        <h3 className="text-[13px] uppercase tracking-[0.14em] font-bold text-[#9DB4E8] mb-3">Counterparty Borrow Requests</h3>
        {requests.pending.length === 0 ? <p className="text-[#5C5C5C] text-sm">No pending borrow requests.</p> : (
          <div className="space-y-3">
            {requests.pending.map((r) => <BorrowRequestRow key={r.id} req={r} accounts={accounts} client={client} onDone={load} />)}
          </div>
        )}
      </div>

      {/* Securities loans */}
      <div>
        <div className="flex items-center gap-3 mb-3">
          <h3 className="text-[13px] uppercase tracking-[0.14em] font-bold text-[#C9A227]">Securities Loans</h3>
          <a href={`${client.defaults.baseURL}/admin/securities/reports/1099-oid.csv`} download onClick={(e) => { e.preventDefault(); download1099(); }} data-testid="securities-1099-export" className="ml-auto inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-[#9DB4E8] hover:text-[#c3d3f5]"><FileDown size={13} /> Export 1099-OID (CSV)</a>
        </div>
        {loans.length === 0 ? <p className="text-[#5C5C5C] text-sm">No securities loans yet.</p> : (
          <div className="space-y-3">
            {loans.map((l) => <LoanAdminRow key={l.id} loan={l} client={client} onChange={load} />)}
          </div>
        )}
      </div>

      <MarketplaceAdmin client={client} market={market} onChange={load} />
      <FundingPrograms client={client} />
    </div>
  );
};

const PayoutsDashboard = ({ client, feeIncome, onChange }) => {
  const rows = feeIncome.fee_income || [];
  const [busyId, setBusyId] = useState(null);
  if (rows.length === 0) return null;
  const badge = (st) => {
    const map = {
      transferred: ["#3DBB8E", "Transferred"],
      transfer_failed: ["#FF8585", "Failed"],
      pending_connect: ["#E4C97A", "Pending onboarding"],
      pending_payout_method: ["#E4C97A", "Pending payout method"],
      remitted: ["#7DD3B8", "Remitted"],
    };
    const [c, l] = map[st] || ["#8A8A8A", st || "—"];
    return <span style={{ color: c, borderColor: c + "55" }} className="text-[10px] uppercase tracking-[0.1em] border rounded-full px-2 py-0.5">{l}</span>;
  };
  const retry = async (id) => {
    setBusyId(id);
    try { const r = await client.post(`/admin/securities/fee-income/${id}/retry-payout`); toast.success(`Payout ${r.data.status}.`); onChange(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusyId(null); }
  };
  return (
    <div data-testid="payouts-dashboard">
      <div className="flex items-center gap-3 mb-3">
        <h3 className="text-[13px] uppercase tracking-[0.14em] font-bold text-[#635BFF]">Lender Payouts</h3>
      </div>
      <div className="flex flex-wrap gap-4 mb-4">
        <Kpi label="Transferred" value={fmt(feeIncome.total_transferred || 0)} color="#3DBB8E" />
        <Kpi label="Pending" value={fmt(feeIncome.total_pending || 0)} color="#E4C97A" />
        <Kpi label="Failed" value={fmt(feeIncome.total_failed || 0)} color="#FF8585" />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]" data-testid="payouts-table">
          <thead><tr className="text-[#8A8A8A] text-left border-b border-[#2A2A22]"><th className="py-2 pr-4">Loan</th><th className="py-2 pr-4">Lender</th><th className="py-2 pr-4">Amount</th><th className="py-2 pr-4">Status</th><th className="py-2 pr-4">Stripe transfer</th><th className="py-2 pr-4"></th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-[#1C1C16]" data-testid={`payout-row-${r.id}`}>
                <td className="py-2 pr-4 font-mono text-[#E4C97A]">{r.loan_no || "—"}</td>
                <td className="py-2 pr-4">{r.lender_name || "—"}</td>
                <td className="py-2 pr-4 text-[#3DBB8E]">{fmt(r.gross)}</td>
                <td className="py-2 pr-4">{badge(r.payout_status)}{r.transfer_error ? <div className="text-[10px] text-[#FF8585] mt-1">{r.transfer_error}</div> : null}</td>
                <td className="py-2 pr-4 font-mono text-[10px] text-[#8A8A8A]">{r.stripe_transfer_id || "—"}</td>
                <td className="py-2 pr-4">{r.payout_status !== "transferred" && (
                  <button onClick={() => retry(r.id)} disabled={busyId === r.id} data-testid={`payout-retry-${r.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] text-[#635BFF] hover:text-[#8b84ff] disabled:opacity-50"><RefreshCw size={11} /> Retry</button>
                )}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const MarketplaceAdmin = ({ client, market, onChange }) => {
  const [form, setForm] = useState({ company: "", instrument_type: "Standby Letter of Credit (SBLC)", face_amount: "", discount_pct: "5", duns_number: "", registered: true });
  const [busy, setBusy] = useState(false);
  const create = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await client.post("/admin/securities/tradelines", { ...form, face_amount: parseFloat(form.face_amount), discount_pct: parseFloat(form.discount_pct) });
      toast.success("Tradeline created & registered on the marketplace.");
      setForm({ company: "", instrument_type: "Standby Letter of Credit (SBLC)", face_amount: "", discount_pct: "5", duns_number: "", registered: true });
      onChange();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const seed = async () => {
    if (!window.confirm("Seed 30 registered tradeline instruments ($5k → $10M ladder)?")) return;
    setBusy(true);
    try { const r = await client.post("/admin/securities/seed-marketplace"); toast.success(`Seeded ${r.data.seeded} tradelines.`); onChange(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const openListings = (market.listings || []).filter((l) => l.status === "open");
  return (
    <div data-testid="marketplace-admin">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <h3 className="text-[13px] uppercase tracking-[0.14em] font-bold text-[#C9A227] flex items-center gap-2"><Store size={15} /> Marketplace · Tradelines</h3>
        <button onClick={seed} disabled={busy} data-testid="admin-seed-marketplace" className="ml-auto inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-[#9DB4E8] hover:text-[#c3d3f5] disabled:opacity-50"><Sparkles size={13} /> Seed 30 ($5k→$10M)</button>
      </div>
      <div className="flex flex-wrap gap-4 mb-5">
        <Kpi label="Open listings" value={openListings.length} color="#C9A227" />
        <Kpi label="Tradelines sold" value={(market.tradelines || []).length} color="#3DBB8E" />
        <Kpi label="Tradeline revenue" value={fmt(market.tradeline_revenue)} color="#7DD3B8" />
        <Kpi label="Active resales" value={(market.resale_active || []).length} color="#E4C97A" />
        <Kpi label="Resale volume" value={fmt(market.resale_revenue)} color="#9DB4E8" />
      </div>

      <form onSubmit={create} className="bg-[#0F0F0A] border border-[#2A2A22] rounded-lg p-5 mb-6 grid sm:grid-cols-2 gap-3" data-testid="admin-create-tradeline-form">
        <div className="sm:col-span-2 text-[12px] uppercase tracking-[0.14em] text-[#8A8A8A]">Create & register a tradeline</div>
        <input required placeholder="Company / entity" value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} data-testid="admin-tl-company" className="bg-[#13130E] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
        <select value={form.instrument_type} onChange={(e) => setForm((f) => ({ ...f, instrument_type: e.target.value }))} data-testid="admin-tl-type" className="bg-[#13130E] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none">
          {["Standby Letter of Credit (SBLC)", "Letter of Credit", "Surety Bond", "Proof of Funds"].map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <input required type="number" min="1" placeholder="Face amount ($)" value={form.face_amount} onChange={(e) => setForm((f) => ({ ...f, face_amount: e.target.value }))} data-testid="admin-tl-face" className="bg-[#13130E] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
        <input required type="number" min="0" step="0.01" placeholder="Rate / discount %" value={form.discount_pct} onChange={(e) => setForm((f) => ({ ...f, discount_pct: e.target.value }))} data-testid="admin-tl-rate" className="bg-[#13130E] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
        <input placeholder="DUNS number (optional)" value={form.duns_number} onChange={(e) => setForm((f) => ({ ...f, duns_number: e.target.value }))} data-testid="admin-tl-duns" className="bg-[#13130E] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
        <label className="flex items-center gap-2 text-[12px] text-[#9A9A90]"><input type="checkbox" checked={form.registered} onChange={(e) => setForm((f) => ({ ...f, registered: e.target.checked }))} data-testid="admin-tl-registered" /> Mark registered</label>
        <button type="submit" disabled={busy} data-testid="admin-tl-create" className="sm:col-span-2 inline-flex items-center justify-center gap-2 bg-[#C9A227] hover:bg-[#b8901f] text-[#0A0A0A] py-2.5 rounded-md text-[12px] uppercase tracking-[0.14em] font-bold disabled:opacity-50"><Plus size={14} /> Create tradeline</button>
      </form>

      {(market.resale_active || []).length > 0 && (
        <div className="mb-6">
          <h4 className="text-[12px] uppercase tracking-[0.14em] text-[#E4C97A] mb-2 flex items-center gap-2"><Repeat size={13} /> Active resale listings</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]" data-testid="admin-resale-table">
              <thead><tr className="text-[#8A8A8A] text-left border-b border-[#2A2A22]"><th className="py-2 pr-4">Tradeline</th><th className="py-2 pr-4">Instrument</th><th className="py-2 pr-4">Seller</th><th className="py-2 pr-4">Resale price</th></tr></thead>
              <tbody>
                {market.resale_active.map((t) => (
                  <tr key={t.id} className="border-b border-[#1C1C16]"><td className="py-2 pr-4 font-mono text-[#E4C97A]">{t.tradeline_no}</td><td className="py-2 pr-4">{t.instrument_type}</td><td className="py-2 pr-4">{(t.resale || {}).seller_name || "—"}</td><td className="py-2 pr-4 text-[#3DBB8E]">{fmt((t.resale || {}).price)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(market.transfers || []).length > 0 && (
        <div>
          <h4 className="text-[12px] uppercase tracking-[0.14em] text-[#9DB4E8] mb-2">Completed transfers</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]" data-testid="admin-transfers-table">
              <thead><tr className="text-[#8A8A8A] text-left border-b border-[#2A2A22]"><th className="py-2 pr-4">Tradeline</th><th className="py-2 pr-4">From</th><th className="py-2 pr-4">To</th><th className="py-2 pr-4">Price</th><th className="py-2 pr-4">Date</th></tr></thead>
              <tbody>
                {market.transfers.map((h, i) => (
                  <tr key={i} className="border-b border-[#1C1C16]"><td className="py-2 pr-4 font-mono text-[#E4C97A]">{h.tradeline_no}</td><td className="py-2 pr-4">{h.from || "—"}</td><td className="py-2 pr-4">{h.to || "—"}</td><td className="py-2 pr-4 text-[#3DBB8E]">{fmt(h.price)}</td><td className="py-2 pr-4 text-[#8A8A8A]">{(h.at || "").slice(0, 10)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const LOAN_STATUS_STYLE = {
  pending_signature: "bg-[#B8860B]/15 text-[#E4C97A] border-[#B8860B]/40",
  executed: "bg-[#0B6B4F]/15 text-[#3DBB8E] border-[#0B6B4F]/40",
  active: "bg-[#0B6B4F]/15 text-[#3DBB8E] border-[#0B6B4F]/40",
  recall_requested: "bg-[#B8860B]/15 text-[#E4C97A] border-[#B8860B]/40",
  returned: "bg-[#3a3a30]/40 text-[#9A9A90] border-[#3a3a30]",
  terminated: "bg-[#7a2222]/20 text-[#FF8585] border-[#7a2222]/50",
};

const BorrowRequestRow = ({ req, accounts, client, onDone }) => {
  const active = accounts.filter((a) => a.status === "active");
  const [accountId, setAccountId] = useState(active[0]?.id || "");
  const [rate, setRate] = useState(0.5);
  const [busy, setBusy] = useState(false);
  const assign = async () => {
    setBusy(true);
    try { const r = await client.post(`/admin/securities/loan-requests/${req.id}/assign`, { account_id: accountId, counterparty_fee_rate: parseFloat(rate) }); toast.success(`Loan ${r.data.loan_no} issued & sign link emailed.`); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const reject = async () => {
    try { await client.post(`/admin/securities/loan-requests/${req.id}/reject`, {}); toast.success("Request rejected."); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  const issueFromInstrument = async () => {
    setBusy(true);
    try { const r = await client.post(`/admin/securities/loan-requests/${req.id}/issue-from-instrument`, { counterparty_fee_rate: parseFloat(rate) }); toast.success(`Loan ${r.data.loan_no} issued from instrument & sign link emailed.`); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-[#2A2A22] bg-[#13130E] p-4" data-testid={`securities-request-${req.id}`}>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-3">
        <div><span className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">Counterparty</span><div className="text-[13px]">{req.counterparty_name}{req.counterparty_entity ? ` · ${req.counterparty_entity}` : ""}</div></div>
        <div><span className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">Requested</span><div className="text-[13px] text-[#E4C97A]">{fmt(req.amount)} · {req.term_days}d</div></div>
        <div><span className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">Instrument</span><div className="text-[13px]">{req.instrument_type || "—"}</div></div>
        <div><span className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">Email</span><div className="text-[13px]">{req.counterparty_email}</div></div>
        {req.source === "marketplace" && <div><span className="text-[10px] uppercase tracking-[0.12em] text-[#9DB4E8]">Marketplace</span><div className="text-[13px] text-[#9DB4E8]">{req.listing_company || req.listing_account_no || "instrument"}</div></div>}
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">Match to account</span>
          <select value={accountId} onChange={(e) => setAccountId(e.target.value)} data-testid={`securities-request-account-${req.id}`}
            className="mt-1 block bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none">
            {active.length === 0 ? <option value="">No active accounts</option> : active.map((a) => <option key={a.id} value={a.id}>{`${a.account_no} · avail ${fmt(a.available)}`}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">CP fee % p.a.</span>
          <input type="number" value={rate} onChange={(e) => setRate(e.target.value)} data-testid={`securities-request-rate-${req.id}`}
            className="mt-1 w-24 bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
        </label>
        <button onClick={assign} disabled={busy || !accountId} data-testid={`securities-request-assign-${req.id}`} className="inline-flex items-center gap-1.5 bg-[#0B6B4F] hover:bg-[#0d7d5c] disabled:opacity-50 text-white px-4 py-2 rounded-md text-[11px] uppercase tracking-[0.12em] font-bold"><CheckCircle2 size={14} /> Match &amp; issue loan</button>
        {req.source === "marketplace" && req.listing_id && (
          <button onClick={issueFromInstrument} disabled={busy} data-testid={`securities-request-issue-instrument-${req.id}`} className="inline-flex items-center gap-1.5 bg-[#9DB4E8] hover:bg-[#c3d3f5] disabled:opacity-50 text-[#0A0A0A] px-4 py-2 rounded-md text-[11px] uppercase tracking-[0.12em] font-bold"><Repeat size={14} /> Issue from instrument</button>
        )}
        <button onClick={reject} data-testid={`securities-request-reject-${req.id}`} className="inline-flex items-center gap-1.5 border border-[#7a2222] text-[#FF8585] hover:bg-[#7a2222]/20 px-4 py-2 rounded-md text-[11px] uppercase tracking-[0.12em] font-bold"><XCircle size={14} /> Reject</button>
      </div>
    </div>
  );
};

const LoanAdminRow = ({ loan, client, onChange }) => {
  const [tx, setTx] = useState({ open: false, channel: "email", to: "", busy: false });
  const setStatus = async (status) => {
    try { await client.post(`/admin/securities/loans/${loan.id}/status`, { status }); toast.success(`Loan ${status}.`); onChange(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const agreement = async () => {
    const r = await client.get(`/admin/securities/loans/${loan.id}/agreement.pdf`, { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  };
  const reminder = async () => {
    try { await client.post(`/admin/securities/loans/${loan.id}/send-reminder`); toast.success("Reminder sent."); } catch (e) { toast.error(errMsg(e)); }
  };
  const send = async () => {
    setTx((t) => ({ ...t, busy: true }));
    try { await client.post(`/admin/securities/loans/${loan.id}/transmit`, { channel: tx.channel, to: tx.to }); toast.success(`Sent via ${tx.channel}.`); setTx({ open: false, channel: "email", to: "", busy: false }); }
    catch (e) { toast.error(errMsg(e)); setTx((t) => ({ ...t, busy: false })); }
  };
  return (
    <div className="rounded-lg border border-[#2A2A22] bg-[#13130E] p-4" data-testid={`securities-admin-loan-${loan.id}`}>
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[180px]">
          <div className="font-mono text-[13px] text-[#E4C97A]">{loan.loan_no}</div>
          <div className="text-[12px] text-[#9A9A90]">{loan.account_no} → {loan.counterparty_name} · <span className="text-[#C9A227]">{loan.type_label}</span></div>
        </div>
        <div className="text-right"><div className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">Loaned</div><div className="text-[15px] font-serif text-[#C9A227]">{fmt(loan.amount)}</div></div>
        <div className="text-right"><div className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">Orig. fee</div><div className={`text-[12px] ${loan.origination_fee_paid ? "text-[#3DBB8E]" : "text-[#E4C97A]"}`}>{fmt(loan.origination_fee)} {loan.origination_fee_paid ? "✓" : "due"}</div></div>
        <div className="text-right"><div className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">CP fee</div><div className={`text-[12px] ${loan.counterparty_fee_paid ? "text-[#3DBB8E]" : "text-[#E4C97A]"}`}>{fmt(loan.counterparty_fee)} {loan.counterparty_fee_paid ? "✓ paid→lender" : "due"}</div></div>
        {loan.days_to_maturity != null && <div className="text-right"><div className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">Matures</div><div className="text-[12px] text-[#9A9A90]">{loan.days_to_maturity}d</div></div>}
        <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.1em] font-bold rounded-full border ${LOAN_STATUS_STYLE[loan.status] || LOAN_STATUS_STYLE.returned}`}>{(loan.status || "").replace("_", " ")}</span>
      </div>
      {loan.notary && <div className="mt-2 text-[11px] text-[#E4C97A]">✓ Notarized · {loan.notary.name} ({loan.notary.commission_no})</div>}
      <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px]">
        <span className="text-[#8A8A8A] uppercase tracking-[0.12em]">{loan.fully_executed ? "✓ Executed" : "Awaiting signatures"} · set:</span>
        {["active", "returned", "terminated"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`securities-loan-status-${s}-${loan.id}`}
            className={`px-3 py-1 rounded-full border text-[10px] uppercase tracking-[0.1em] ${loan.status === s ? "border-[#C9A227] text-[#C9A227]" : "border-[#2A2A22] text-[#8A8A8A] hover:text-[#ECECE4]"}`}>{s}</button>
        ))}
        <span className="text-[#3a3a30]">|</span>
        <button onClick={agreement} data-testid={`securities-admin-loan-agreement-${loan.id}`} className="inline-flex items-center gap-1 text-[#9DB4E8] hover:text-[#c3d3f5]"><Download size={12} /> Agreement</button>
        <button onClick={() => setTx((t) => ({ ...t, open: !t.open }))} data-testid={`securities-admin-loan-transmit-${loan.id}`} className="inline-flex items-center gap-1 text-[#9DB4E8] hover:text-[#c3d3f5]"><Send size={12} /> Transmit</button>
        <button onClick={reminder} data-testid={`securities-admin-loan-reminder-${loan.id}`} className="inline-flex items-center gap-1 text-[#8A8A8A] hover:text-[#ECECE4]"><Bell size={12} /> Reminder</button>
      </div>
      {tx.open && (
        <div className="mt-3 flex flex-wrap items-end gap-3 bg-[#0F0F0A] border border-[#2A2A22] rounded-md p-3">
          <select value={tx.channel} onChange={(e) => setTx((t) => ({ ...t, channel: e.target.value }))} data-testid={`securities-admin-loan-tx-channel-${loan.id}`}
            className="bg-[#13130E] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none">
            <option value="email">Email (PDF)</option><option value="fax">eFax</option>
          </select>
          <input value={tx.to} onChange={(e) => setTx((t) => ({ ...t, to: e.target.value }))} placeholder={tx.channel === "fax" ? "+13125551234" : "counterparty@bank.com"} data-testid={`securities-admin-loan-tx-to-${loan.id}`}
            className="flex-1 min-w-[180px] bg-[#13130E] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
          <button onClick={send} disabled={tx.busy || !tx.to} data-testid={`securities-admin-loan-tx-send-${loan.id}`} className="inline-flex items-center gap-1.5 bg-[#0B6B4F] hover:bg-[#0d7d5c] disabled:opacity-50 text-white px-4 py-2 rounded-md text-[11px] uppercase tracking-[0.12em] font-bold">{tx.busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send</button>
        </div>
      )}
    </div>
  );
};

const Kpi = ({ label, value, color }) => (
  <div className="rounded-lg border border-[#2A2A22] bg-[#13130E] px-5 py-3 min-w-[160px]">
    <div className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">{label}</div>
    <div className="text-xl font-serif mt-1" style={{ color }}>{value}</div>
  </div>
);

const PendingApp = ({ app, client, onDone }) => {
  const [amount, setAmount] = useState(app.requested_amount || "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const approve = async () => {
    setBusy(true);
    try {
      await client.post(`/admin/securities/applications/${app.id}/approve`, { approved_amount: parseFloat(amount), note: note || null });
      toast.success("Account provisioned.");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const reject = async () => {
    setBusy(true);
    try {
      await client.post(`/admin/securities/applications/${app.id}/reject`, { note: note || null });
      toast.success("Application rejected.");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-[#2A2A22] bg-[#13130E] p-4" data-testid={`securities-pending-${app.id}`}>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-3">
        <div><span className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">Applicant</span><div className="text-[13px]">{app.holder_name} · {app.company}</div></div>
        <div><span className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">Type</span><div className="text-[13px]">{app.type_label}</div></div>
        <div><span className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">Requested</span><div className="text-[13px] text-[#E4C97A]">{fmt(app.requested_amount)}</div></div>
        {app.beneficiary_name && <div><span className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">Beneficiary</span><div className="text-[13px]">{app.beneficiary_name}</div></div>}
      </div>
      {app.purpose && <div className="text-[12px] text-[#9A9A90] mb-3">Purpose: {app.purpose}</div>}
      <div className="flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">Approved amount (USD)</span>
          <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid={`securities-approve-amount-${app.id}`}
            className="mt-1 w-40 bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
        </label>
        <label className="block flex-1 min-w-[160px]">
          <span className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">Note (optional)</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} data-testid={`securities-review-note-${app.id}`}
            className="mt-1 w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
        </label>
        <button onClick={approve} disabled={busy} data-testid={`securities-approve-${app.id}`} className="inline-flex items-center gap-1.5 bg-[#0B6B4F] hover:bg-[#0d7d5c] disabled:opacity-50 text-white px-4 py-2 rounded-md text-[11px] uppercase tracking-[0.12em] font-bold"><CheckCircle2 size={14} /> Approve & provision</button>
        <button onClick={reject} disabled={busy} data-testid={`securities-reject-${app.id}`} className="inline-flex items-center gap-1.5 border border-[#7a2222] text-[#FF8585] hover:bg-[#7a2222]/20 px-4 py-2 rounded-md text-[11px] uppercase tracking-[0.12em] font-bold"><XCircle size={14} /> Reject</button>
      </div>
    </div>
  );
};

const AccountRow = ({ acc, client, onChange }) => {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [entry, setEntry] = useState({ entry_type: "credit", amount: "", memo: "" });
  const [busy, setBusy] = useState(false);

  const loadDetail = async () => {
    const r = await client.get(`/admin/securities/accounts/${acc.id}`);
    setDetail(r.data.account);
  };
  const toggle = async () => { if (!open && !detail) await loadDetail(); setOpen((o) => !o); };

  const addEntry = async () => {
    setBusy(true);
    try {
      await client.post(`/admin/securities/accounts/${acc.id}/ledger`, {
        entry_type: entry.entry_type, amount: parseFloat(entry.amount), memo: entry.memo || null,
      });
      toast.success(`${entry.entry_type === "credit" ? "Credit" : "Debit"} posted.`);
      setEntry({ entry_type: "credit", amount: "", memo: "" });
      await loadDetail();
      onChange();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const setStatus = async (status) => {
    try {
      await client.post(`/admin/securities/accounts/${acc.id}/status`, { status });
      toast.success(`Account ${status}.`);
      onChange();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const statement = async () => {
    const r = await client.get(`/admin/securities/accounts/${acc.id}/statement.pdf`, { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  };
  const markFeePaid = async () => {
    try { await client.post(`/admin/securities/accounts/${acc.id}/fee/mark-paid`); toast.success("Facility fee marked paid."); onChange(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="rounded-lg border border-[#2A2A22] bg-[#13130E]" data-testid={`securities-admin-account-${acc.id}`}>
      <div className="p-4 flex flex-wrap items-center gap-4">
        <button onClick={toggle} className="text-[#8A8A8A] hover:text-[#ECECE4]">{open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</button>
        <div className="flex-1 min-w-[180px]">
          <div className="font-mono text-[14px] text-[#E4C97A]">{acc.account_no}</div>
          <div className="text-[12px] text-[#9A9A90]">{acc.type_label} · {acc.holder_name || acc.company}</div>
          <div className="text-[11px] text-[#8A8A8A] mt-0.5">On loan {fmt(acc.on_loan)} · Available {fmt(acc.available)}</div>
        </div>
        <div className="text-right"><div className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">Balance</div><div className="text-lg font-serif text-[#3DBB8E]" data-testid={`securities-admin-balance-${acc.id}`}>{fmt(acc.balance)}</div></div>
        <div className="text-right"><div className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">Facility fee</div><div className={`text-[12px] ${acc.facility_fee_paid ? "text-[#3DBB8E]" : "text-[#E4C97A]"}`} data-testid={`securities-admin-facility-fee-${acc.id}`}>{fmt(acc.facility_fee)} {acc.facility_fee_paid ? "✓ paid" : "due"}</div></div>
        <Badge s={acc.status} />
        {!acc.facility_fee_paid && Number(acc.facility_fee) > 0 && (
          <button onClick={markFeePaid} data-testid={`securities-mark-fee-paid-${acc.id}`} className="text-[11px] uppercase tracking-[0.12em] text-[#C9A227] hover:text-[#e0b62e]">Mark fee paid</button>
        )}
        <button onClick={statement} data-testid={`securities-admin-statement-${acc.id}`} className="inline-flex items-center gap-1.5 text-[11px] text-[#9DB4E8] hover:text-[#c3d3f5]"><Download size={13} /> Statement</button>
      </div>
      {open && detail && (
        <div className="border-t border-[#1C1C16] p-4 space-y-4">
          {/* Add ledger entry */}
          <div className="flex flex-wrap items-end gap-3 bg-[#0F0F0A] rounded-md p-3 border border-[#2A2A22]">
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">Entry</span>
              <select value={entry.entry_type} onChange={(e) => setEntry((x) => ({ ...x, entry_type: e.target.value }))} data-testid={`securities-entry-type-${acc.id}`}
                className="mt-1 block bg-[#13130E] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none">
                <option value="credit">Credit (+)</option>
                <option value="debit">Debit (−)</option>
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">Amount (USD)</span>
              <input type="number" value={entry.amount} onChange={(e) => setEntry((x) => ({ ...x, amount: e.target.value }))} data-testid={`securities-entry-amount-${acc.id}`}
                className="mt-1 w-36 bg-[#13130E] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
            </label>
            <label className="block flex-1 min-w-[160px]">
              <span className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">Memo</span>
              <input value={entry.memo} onChange={(e) => setEntry((x) => ({ ...x, memo: e.target.value }))} data-testid={`securities-entry-memo-${acc.id}`}
                className="mt-1 w-full bg-[#13130E] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
            </label>
            <button onClick={addEntry} disabled={busy || !entry.amount} data-testid={`securities-post-entry-${acc.id}`} className="inline-flex items-center gap-1.5 bg-[#C9A227] hover:bg-[#b8901f] disabled:opacity-50 text-[#0A0A0A] px-4 py-2 rounded-md text-[11px] uppercase tracking-[0.12em] font-bold">
              {entry.entry_type === "credit" ? <Plus size={14} /> : <Minus size={14} />} Post entry
            </button>
          </div>
          {/* Status controls */}
          <div className="flex items-center gap-2 text-[11px]">
            <span className="text-[#8A8A8A] uppercase tracking-[0.12em]">Status:</span>
            {["active", "suspended", "closed"].map((s) => (
              <button key={s} onClick={() => setStatus(s)} data-testid={`securities-status-${s}-${acc.id}`}
                className={`px-3 py-1 rounded-full border text-[10px] uppercase tracking-[0.1em] ${acc.status === s ? "border-[#C9A227] text-[#C9A227]" : "border-[#2A2A22] text-[#8A8A8A] hover:text-[#ECECE4]"}`}>{s}</button>
            ))}
          </div>
          {/* Ledger */}
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead className="text-[#8A8A8A] text-[10px] uppercase tracking-[0.1em]"><tr><th className="text-left py-2">Date</th><th className="text-left py-2">Memo</th><th className="text-right py-2">Credit</th><th className="text-right py-2">Debit</th><th className="text-right py-2">Balance</th></tr></thead>
              <tbody>
                {(detail.ledger || []).map((e) => (
                  <tr key={e.id} className="border-t border-[#1C1C16]">
                    <td className="py-2 text-[#9A9A90]">{(e.created_at || "").slice(0, 10)}</td>
                    <td className="py-2">{e.memo || "—"}</td>
                    <td className="py-2 text-right text-[#3DBB8E]">{e.entry_type === "credit" ? fmt(e.amount) : ""}</td>
                    <td className="py-2 text-right text-[#FF8585]">{e.entry_type === "debit" ? fmt(e.amount) : ""}</td>
                    <td className="py-2 text-right font-mono">{fmt(e.balance_after)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};


// ---------------------------------------------------------------------------
// Master Private Negotiable Instrument Funding Program — Admin
// ---------------------------------------------------------------------------
const FundingPrograms = ({ client }) => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    program_name: "", issuer_name: "SEUTRADE FINANCIAL SERVICES", issuer_entity: "EIN 42-6951310",
    acceptor_name: "SEUTRADE FINANCIAL SERVICES SPV Trust, as Acceptor", program_limit: "",
    discount_rate: "4.0", default_tenor_days: "90", governing_law: "State of Delaware, USA",
    collateralized: true, trust_custodian: "SEUTRADE FINANCIAL SERVICES SPV Trust",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await client.get("/admin/funding/programs"); setPrograms(r.data.programs || []); }
    catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [client]);
  useEffect(() => { load(); }, [load]);

  const seed = async () => {
    setBusy(true);
    try {
      const r = await client.post("/admin/funding/seed");
      toast.success(r.data.seeded ? "Seeded the pre-funded $100,000,000 Master Program." : "Flagship program already exists.");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await client.post("/admin/funding/programs", {
        ...form,
        program_limit: Number(form.program_limit),
        discount_rate: Number(form.discount_rate),
        default_tenor_days: Number(form.default_tenor_days),
      });
      toast.success("Master Agreement executed & registered.");
      setShowCreate(false);
      setForm((f) => ({ ...f, program_name: "", program_limit: "" }));
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const totalLimit = programs.reduce((s, p) => s + (p.program_limit || 0), 0);
  const totalHeadroom = programs.reduce((s, p) => s + (p.available_headroom || 0), 0);

  return (
    <div data-testid="funding-programs-admin" className="pt-4 border-t border-[#2A2A22]">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <h3 className="text-[13px] uppercase tracking-[0.14em] font-bold text-[#C9A227] flex items-center gap-2"><ScrollText size={15} /> Master Private Negotiable Instrument Funding Program</h3>
        <a href="/funding" target="_blank" rel="noreferrer" data-testid="funding-public-registry-link" className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.12em] text-[#9DB4E8] hover:text-[#c3d3f5]"><ExternalLink size={12} /> Public registry</a>
        <div className="ml-auto flex items-center gap-4">
          <button onClick={seed} disabled={busy} data-testid="funding-seed-btn" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-[#3DBB8E] hover:text-[#6fe0b8] disabled:opacity-50"><Sparkles size={13} /> Seed $100M program</button>
          <button onClick={() => setShowCreate((s) => !s)} data-testid="funding-toggle-create" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-[#C9A227] hover:text-[#e4c97a]"><Plus size={13} /> New program</button>
        </div>
      </div>
      <div className="flex flex-wrap gap-4 mb-5">
        <Kpi label="Programs" value={programs.length} color="#C9A227" />
        <Kpi label="Total revolving capacity" value={fmt(totalLimit)} color="#3DBB8E" />
        <Kpi label="Available headroom" value={fmt(totalHeadroom)} color="#7DD3B8" />
      </div>

      {showCreate && (
        <form onSubmit={create} className="bg-[#0F0F0A] border border-[#2A2A22] rounded-lg p-5 mb-6 grid sm:grid-cols-2 gap-3" data-testid="funding-create-form">
          <div className="sm:col-span-2 text-[12px] uppercase tracking-[0.14em] text-[#8A8A8A]">Execute a new Master Agreement</div>
          <input required placeholder="Program name" value={form.program_name} onChange={(e) => setForm((f) => ({ ...f, program_name: e.target.value }))} data-testid="funding-f-name" className="sm:col-span-2 bg-[#13130E] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
          <input required placeholder="Issuer / drawer" value={form.issuer_name} onChange={(e) => setForm((f) => ({ ...f, issuer_name: e.target.value }))} data-testid="funding-f-issuer" className="bg-[#13130E] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
          <input placeholder="Issuer entity (EIN)" value={form.issuer_entity} onChange={(e) => setForm((f) => ({ ...f, issuer_entity: e.target.value }))} data-testid="funding-f-entity" className="bg-[#13130E] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
          <input placeholder="Acceptor / drawee" value={form.acceptor_name} onChange={(e) => setForm((f) => ({ ...f, acceptor_name: e.target.value }))} data-testid="funding-f-acceptor" className="sm:col-span-2 bg-[#13130E] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
          <input required type="number" min="1" placeholder="Revolving program limit ($)" value={form.program_limit} onChange={(e) => setForm((f) => ({ ...f, program_limit: e.target.value }))} data-testid="funding-f-limit" className="bg-[#13130E] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
          <input type="number" min="0" step="0.01" placeholder="Discount rate %" value={form.discount_rate} onChange={(e) => setForm((f) => ({ ...f, discount_rate: e.target.value }))} data-testid="funding-f-rate" className="bg-[#13130E] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
          <input type="number" min="1" placeholder="Default tenor (days)" value={form.default_tenor_days} onChange={(e) => setForm((f) => ({ ...f, default_tenor_days: e.target.value }))} data-testid="funding-f-tenor" className="bg-[#13130E] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
          <input placeholder="Governing law" value={form.governing_law} onChange={(e) => setForm((f) => ({ ...f, governing_law: e.target.value }))} data-testid="funding-f-law" className="bg-[#13130E] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
          <label className="flex items-center gap-2 text-[12px] text-[#9A9A90]"><input type="checkbox" checked={form.collateralized} onChange={(e) => setForm((f) => ({ ...f, collateralized: e.target.checked }))} data-testid="funding-f-collateralized" /> Collateralized (Security Agreement)</label>
          <button type="submit" disabled={busy} data-testid="funding-create-submit" className="sm:col-span-2 inline-flex items-center justify-center gap-2 bg-[#C9A227] hover:bg-[#b8901f] text-[#0A0A0A] py-2.5 rounded-md text-[12px] uppercase tracking-[0.14em] font-bold disabled:opacity-50"><Plus size={14} /> Execute & register</button>
        </form>
      )}

      {loading ? <Loader2 className="animate-spin text-[#8A8A8A]" /> : programs.length === 0 ? (
        <p className="text-[#5C5C5C] text-sm">No funding programs yet. Seed the $100M flagship or create one.</p>
      ) : (
        <div className="space-y-3">
          {programs.map((p) => <ProgramRow key={p.id} program={p} client={client} onChange={load} />)}
        </div>
      )}

      <DiscountRequests client={client} />
    </div>
  );
};

const DiscountRequests = ({ client }) => {
  const [reqs, setReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await client.get("/admin/funding/discount-requests"); setReqs(r.data.requests || []); }
    catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [client]);
  useEffect(() => { load(); }, [load]);

  const resolve = async (id, status) => {
    try { await client.post(`/admin/funding/discount-requests/${id}/resolve`, { status }); toast.success(`Request ${status}.`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const stColor = { submitted: "#E4C97A", acknowledged: "#9DB4E8", settled: "#3DBB8E", declined: "#FF8585" };

  if (loading) return null;
  if (reqs.length === 0) return null;
  return (
    <div className="mt-8" data-testid="funding-discount-requests">
      <h4 className="text-[12px] uppercase tracking-[0.14em] text-[#E4C97A] mb-3 flex items-center gap-2"><Repeat size={13} /> Draft Discount Requests</h4>
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]" data-testid="funding-discount-table">
          <thead><tr className="text-[#8A8A8A] text-left border-b border-[#2A2A22]"><th className="py-2 pr-4">Draft</th><th className="py-2 pr-4">Counterparty</th><th className="py-2 pr-4">Bank</th><th className="py-2 pr-4">Face</th><th className="py-2 pr-4">Status</th><th className="py-2 pr-4"></th></tr></thead>
          <tbody>
            {reqs.map((r) => (
              <tr key={r.id} className="border-b border-[#1C1C16]" data-testid={`funding-discount-row-${r.id}`}>
                <td className="py-2 pr-4 font-mono text-[#E4C97A]">{r.draft_no}</td>
                <td className="py-2 pr-4">{r.counterparty_name}<div className="text-[10px] text-[#8A8A8A]">{r.counterparty_email}</div></td>
                <td className="py-2 pr-4 text-[#9A9A90]">{r.bank_name || "—"}</td>
                <td className="py-2 pr-4 text-[#3DBB8E]">{fmt(r.face_amount)}</td>
                <td className="py-2 pr-4"><span style={{ color: stColor[r.status] || "#8A8A8A", borderColor: (stColor[r.status] || "#8A8A8A") + "55" }} className="text-[10px] uppercase tracking-[0.1em] border rounded-full px-2 py-0.5">{r.status}</span></td>
                <td className="py-2 pr-4 whitespace-nowrap">
                  {r.status === "submitted" && <button onClick={() => resolve(r.id, "acknowledged")} data-testid={`funding-discount-ack-${r.id}`} className="text-[10px] text-[#9DB4E8] hover:text-[#c3d3f5] mr-3">Acknowledge</button>}
                  {r.status !== "settled" && r.status !== "declined" && <button onClick={() => resolve(r.id, "settled")} data-testid={`funding-discount-settle-${r.id}`} className="text-[10px] text-[#3DBB8E] hover:text-[#6fe0b8] mr-3">Settle</button>}
                  {r.status !== "declined" && r.status !== "settled" && <button onClick={() => resolve(r.id, "declined")} data-testid={`funding-discount-decline-${r.id}`} className="text-[10px] text-[#FF8585] hover:text-[#ff6060]">Decline</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ProgramRow = ({ program, client, onChange }) => {
  const [open, setOpen] = useState(false);
  const [register, setRegister] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({ face_amount: "", payee_name: "", payee_entity: "", payee_email: "", tenor_days: "", discount_rate: "", purpose: "" });
  const [worksheet, setWorksheet] = useState(null);

  const openWorksheet = async (d) => {
    try { const r = await client.get(`/admin/funding/drafts/${d.id}/tn-ucc1-worksheet`); setWorksheet(r.data); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const loadRegister = useCallback(async () => {
    try { const r = await client.get(`/admin/funding/programs/${program.id}/register`); setRegister(r.data); }
    catch (e) { toast.error(errMsg(e)); }
  }, [client, program.id]);

  const toggle = () => { const n = !open; setOpen(n); if (n && !register) loadRegister(); };

  const downloadPdf = async (url, filename) => {
    try {
      const r = await client.get(url, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = u; a.download = filename; a.click();
      URL.revokeObjectURL(u);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const issue = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await client.post("/admin/funding/drafts", {
        program_id: program.id,
        face_amount: Number(draft.face_amount),
        payee_name: draft.payee_name,
        payee_entity: draft.payee_entity || null,
        payee_email: draft.payee_email || null,
        tenor_days: draft.tenor_days ? Number(draft.tenor_days) : null,
        discount_rate: draft.discount_rate !== "" ? Number(draft.discount_rate) : null,
        purpose: draft.purpose || null,
      });
      toast.success("Private Negotiable Instrument issued.");
      setDraft({ face_amount: "", payee_name: "", payee_entity: "", payee_email: "", tenor_days: "", discount_rate: "", purpose: "" });
      await loadRegister();
      onChange();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const assignDraft = async (d) => {
    const name = window.prompt(`Assign draft ${d.draft_no} to (assignee name):`);
    if (!name) return;
    const entity = window.prompt("Assignee entity (optional):") || null;
    try { await client.post(`/admin/funding/drafts/${d.id}/assign`, { assignee_name: name, assignee_entity: entity }); toast.success("Draft assigned."); await loadRegister(); onChange(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const changeStatus = async (d, status) => {
    if (!status || status === d.status) return;
    try { await client.post(`/admin/funding/drafts/${d.id}/status`, { status }); toast.success(`Draft marked ${status}.`); await loadRegister(); onChange(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const projUtil = program.program_limit ? ((program.outstanding_total + Number(draft.face_amount || 0)) / program.program_limit) * 100 : 0;
  const faceNum = Number(draft.face_amount || 0);
  const overHeadroom = faceNum > program.available_headroom;
  const nearCap = !overHeadroom && faceNum > 0 && projUtil >= 90;

  const util = program.program_limit ? Math.min(100, (program.outstanding_total / program.program_limit) * 100) : 0;
  const base = client.defaults.baseURL;

  return (
    <div className="bg-[#0F0F0A] border border-[#2A2A22] rounded-lg" data-testid={`funding-program-${program.id}`}>
      <button onClick={toggle} className="w-full flex items-center gap-3 px-5 py-4 text-left" data-testid={`funding-program-toggle-${program.id}`}>
        {open ? <ChevronDown size={16} className="text-[#8A8A8A]" /> : <ChevronRight size={16} className="text-[#8A8A8A]" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[#E4C97A] text-[13px]">{program.program_no}</span>
            {program.executed && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] text-[#3DBB8E] border border-[#0B6B4F]/40 rounded-full px-2 py-0.5"><ShieldCheck size={11} /> Executed</span>}
          </div>
          <div className="text-[13px] text-[#ECECE4] truncate">{program.program_name}</div>
        </div>
        <div className="text-right hidden sm:block">
          <div className="text-[11px] text-[#8A8A8A]">Headroom</div>
          <div className="text-[13px] font-serif text-[#3DBB8E]">{fmt(program.available_headroom)}</div>
        </div>
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-5 border-t border-[#1C1C16] pt-4">
          <div className="flex flex-wrap gap-4">
            <Kpi label="Revolving limit" value={fmt(program.program_limit)} color="#C9A227" />
            <Kpi label="Outstanding" value={fmt(program.outstanding_total)} color="#9DB4E8" />
            <Kpi label="Available headroom" value={fmt(program.available_headroom)} color="#3DBB8E" />
            <Kpi label="Drafts issued" value={program.drafts_count || 0} color="#E4C97A" />
          </div>
          <div>
            <div className="h-2 rounded-full bg-[#1C1C16] overflow-hidden"><div className="h-full bg-[#C9A227]" style={{ width: `${util}%` }} /></div>
            <div className="text-[10px] text-[#8A8A8A] mt-1">{util.toFixed(1)}% of capacity committed</div>
          </div>

          <div className="flex flex-wrap gap-4 text-[12px]">
            <button onClick={() => downloadPdf(`/admin/funding/programs/${program.id}/agreement.pdf`, `master_agreement_${program.program_no}.pdf`)} data-testid={`funding-agreement-pdf-${program.id}`} className="inline-flex items-center gap-1.5 text-[#9DB4E8] hover:text-[#c3d3f5]"><FileDown size={13} /> Master Agreement PDF</button>
            <a href={`/funding/register/${program.verify_token}`} target="_blank" rel="noreferrer" data-testid={`funding-public-register-${program.id}`} className="inline-flex items-center gap-1.5 text-[#9DB4E8] hover:text-[#c3d3f5]"><ExternalLink size={13} /> Public register</a>
            {program.execution_hash && <span className="text-[10px] font-mono text-[#5C5C5C] self-center">SHA-256 {program.execution_hash.slice(0, 16)}…</span>}
          </div>

          {/* Issue a draft */}
          <form onSubmit={issue} className="bg-[#13130E] border border-[#2A2A22] rounded-md p-4 grid sm:grid-cols-2 gap-3" data-testid={`funding-issue-form-${program.id}`}>
            <div className="sm:col-span-2 text-[11px] uppercase tracking-[0.14em] text-[#8A8A8A]">Issue a Private Negotiable Instrument</div>
            <input required type="number" min="1" placeholder="Face amount ($)" value={draft.face_amount} onChange={(e) => setDraft((d) => ({ ...d, face_amount: e.target.value }))} data-testid={`funding-issue-face-${program.id}`} className="bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
            <input required placeholder="Payee name" value={draft.payee_name} onChange={(e) => setDraft((d) => ({ ...d, payee_name: e.target.value }))} data-testid={`funding-issue-payee-${program.id}`} className="bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
            <input placeholder="Payee entity (optional)" value={draft.payee_entity} onChange={(e) => setDraft((d) => ({ ...d, payee_entity: e.target.value }))} data-testid={`funding-issue-payee-entity-${program.id}`} className="bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
            <input type="email" placeholder="Payee email (optional — sends certificate)" value={draft.payee_email} onChange={(e) => setDraft((d) => ({ ...d, payee_email: e.target.value }))} data-testid={`funding-issue-email-${program.id}`} className="bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
            <input type="number" min="1" placeholder={`Tenor days (default ${program.default_tenor_days})`} value={draft.tenor_days} onChange={(e) => setDraft((d) => ({ ...d, tenor_days: e.target.value }))} data-testid={`funding-issue-tenor-${program.id}`} className="bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
            <input type="number" min="0" step="0.01" placeholder={`Discount % (default ${program.discount_rate})`} value={draft.discount_rate} onChange={(e) => setDraft((d) => ({ ...d, discount_rate: e.target.value }))} data-testid={`funding-issue-rate-${program.id}`} className="bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
            <input placeholder="Purpose (optional)" value={draft.purpose} onChange={(e) => setDraft((d) => ({ ...d, purpose: e.target.value }))} data-testid={`funding-issue-purpose-${program.id}`} className="bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
            {(overHeadroom || nearCap) && (
              <div className={`sm:col-span-2 text-[11px] rounded-md px-3 py-2 border ${overHeadroom ? "text-[#FF8585] border-[#FF8585]/40 bg-[#FF8585]/10" : "text-[#E4C97A] border-[#B8860B]/40 bg-[#B8860B]/10"}`} data-testid={`funding-headroom-alert-${program.id}`}>
                {overHeadroom
                  ? `Exceeds available headroom (${fmt(program.available_headroom)}). Reduce the face amount.`
                  : `Heads up — this draft would commit ${projUtil.toFixed(1)}% of program capacity.`}
              </div>
            )}
            <button type="submit" disabled={busy || overHeadroom} data-testid={`funding-issue-submit-${program.id}`} className="sm:col-span-2 inline-flex items-center justify-center gap-2 bg-[#0B6B4F] hover:bg-[#0d7d5c] text-[#ECECE4] py-2.5 rounded-md text-[12px] uppercase tracking-[0.14em] font-bold disabled:opacity-50"><Plus size={14} /> Issue draft</button>
          </form>

          {/* Draft issuance register */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#8A8A8A] mb-2">Draft Issuance Register</div>
            {!register ? <Loader2 className="animate-spin text-[#8A8A8A]" size={16} /> : (register.drafts || []).length === 0 ? (
              <p className="text-[#5C5C5C] text-sm">No drafts issued yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[12px]" data-testid={`funding-register-table-${program.id}`}>
                  <thead><tr className="text-[#8A8A8A] text-left border-b border-[#2A2A22]"><th className="py-2 pr-4">Draft No.</th><th className="py-2 pr-4">Payee</th><th className="py-2 pr-4">Face</th><th className="py-2 pr-4">Disc.</th><th className="py-2 pr-4">Net</th><th className="py-2 pr-4">Maturity</th><th className="py-2 pr-4">Status</th><th className="py-2 pr-4"></th></tr></thead>
                  <tbody>
                    {register.drafts.map((d) => (
                      <tr key={d.id} className="border-b border-[#1C1C16]" data-testid={`funding-draft-row-${d.id}`}>
                        <td className="py-2 pr-4 font-mono text-[#E4C97A]">{d.draft_no}</td>
                        <td className="py-2 pr-4">{d.payee_name}</td>
                        <td className="py-2 pr-4 text-[#3DBB8E]">{fmt(d.face_amount)}</td>
                        <td className="py-2 pr-4">{d.discount_rate}%</td>
                        <td className="py-2 pr-4 text-[#9DB4E8]">{fmt(d.discounted_value)}</td>
                        <td className="py-2 pr-4 text-[#9A9A90]">{d.maturity_date}</td>
                        <td className="py-2 pr-4"><span className="text-[10px] uppercase tracking-[0.1em] text-[#E4C97A]">{d.status}</span></td>
                        <td className="py-2 pr-4 whitespace-nowrap">
                          <a href={`${base}/funding/drafts/${d.verify_token}/certificate.pdf`} target="_blank" rel="noreferrer" data-testid={`funding-draft-cert-${d.id}`} className="inline-flex items-center gap-1 text-[10px] text-[#9DB4E8] hover:text-[#c3d3f5] mr-3"><Download size={11} /> Draft</a>
                          <a href={`${base}/funding/drafts/${d.verify_token}/ucc1.pdf`} target="_blank" rel="noreferrer" data-testid={`funding-draft-ucc1-${d.id}`} className="inline-flex items-center gap-1 text-[10px] text-[#9DB4E8] hover:text-[#c3d3f5] mr-3"><Download size={11} /> UCC-1</a>
                          <a href={`/funding/verify/${d.verify_token}`} target="_blank" rel="noreferrer" data-testid={`funding-draft-verify-${d.id}`} className="inline-flex items-center gap-1 text-[10px] text-[#3DBB8E] hover:text-[#6fe0b8] mr-3"><ShieldCheck size={11} /> Verify</a>
                          <button onClick={() => assignDraft(d)} data-testid={`funding-draft-assign-${d.id}`} className="inline-flex items-center gap-1 text-[10px] text-[#C9A227] hover:text-[#e4c97a] mr-3">Assign</button>
                          <button onClick={() => openWorksheet(d)} data-testid={`funding-draft-tnfiling-${d.id}`} className="inline-flex items-center gap-1 text-[10px] text-[#9DB4E8] hover:text-[#c3d3f5] mr-3"><ClipboardList size={11} /> TN Filing</button>
                          <select value={d.status} onChange={(e) => changeStatus(d, e.target.value)} data-testid={`funding-draft-status-${d.id}`} className="bg-[#0F0F0A] border border-[#2A2A22] rounded px-1.5 py-1 text-[10px] text-[#ECECE4] focus:border-[#C9A227] outline-none">
                            {["issued", "assigned", "discounted", "settled", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
      {worksheet && <TnFilingModal worksheet={worksheet} onClose={() => setWorksheet(null)} draftToken={(register?.drafts || []).find((x) => x.draft_no === worksheet.draft_no)?.verify_token} base={base} />}
    </div>
  );
};

const CopyField = ({ label, value }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(String(value ?? "")).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1200); });
  };
  return (
    <div className="flex items-start gap-2 py-1.5 border-b border-[#1C1C16]" data-testid="tn-copy-field">
      <div className="w-52 shrink-0 text-[10px] uppercase tracking-[0.08em] text-[#8A8A8A] pt-1">{label}</div>
      <div className="flex-1 text-[12px] text-[#ECECE4] break-words whitespace-pre-wrap">{value || <span className="text-[#5C5C5C]">—</span>}</div>
      <button onClick={copy} className="shrink-0 inline-flex items-center gap-1 text-[10px] text-[#9DB4E8] hover:text-[#c3d3f5] pt-0.5" title="Copy">
        {copied ? <><CheckCircle2 size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
      </button>
    </div>
  );
};

const TnFilingModal = ({ worksheet, onClose, draftToken, base }) => {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto p-4" onClick={onClose} data-testid="tn-filing-modal">
      <div className="bg-[#0F0F0A] border border-[#2A2A22] rounded-xl max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#2A2A22] sticky top-0 bg-[#0F0F0A] z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#B8860B] font-bold">Tennessee SoS · UCC-1 Filing Packet</div>
            <div className="text-[13px] text-[#ECECE4] font-mono">{worksheet.draft_no}</div>
          </div>
          <button onClick={onClose} data-testid="tn-filing-close" className="text-[#8A8A8A] hover:text-[#ECECE4]"><X size={18} /></button>
        </div>
        <div className="px-6 py-4">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <a href="https://tncab.tnsos.gov/portal/Workflow/fillform" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[11px] text-[#9DB4E8] hover:text-[#c3d3f5]"><ExternalLink size={12} /> Open TN filing portal</a>
            {draftToken && <a href={`${base}/funding/drafts/${draftToken}/ucc1.pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[11px] text-[#9DB4E8] hover:text-[#c3d3f5]"><Download size={12} /> UCC-1 PDF to attach</a>}
          </div>
          {Object.entries(worksheet.sections).map(([section, fields]) => (
            <div key={section} className="mb-4">
              <div className="text-[11px] uppercase tracking-[0.14em] text-[#C9A227] mb-1">{section}</div>
              {Object.entries(fields).map(([k, v]) => <CopyField key={k} label={k} value={v} />)}
            </div>
          ))}
          {worksheet.notes && <p className="text-[11px] text-[#8A8A8A] leading-relaxed mt-2">{worksheet.notes}</p>}
        </div>
      </div>
    </div>
  );
};